/**
 * Custom hook for event form save operations
 * Eliminates duplication of 8 nearly-identical handleSave* functions in edit.tsx
 */

import React, { useCallback, useState } from "react";
import { eventApi } from "@dreambigger/shared/src/api/weserve/api-helper";
import { Event as EventType, EventTheme } from "@dreambigger/shared/src/types";
import { message, Banner } from "@dreambigger/design-system/src/components";
import { MessageInstance } from "antd/es/message/interface";
import { COLORS, SUCCESS_BANNER_STYLE } from "../util/constants";

export interface UseEventFormSaveProps {
  eventId: number;
  onSuccess: () => void;
  onNavigateToMain: () => void;
  messageApi: MessageInstance;
  keyword?: string;
}

export interface UseEventFormSaveResult {
  loading: boolean;
  error: string | null;
  loadedEvent: EventType | null;
  setLoadedEvent: (event: EventType) => void;
  fetchEvent: () => Promise<void>;
  displaySuccessBanner: () => void;
  handleSaveName: (values: { name: string }) => Promise<void>;
  handleSaveLocation: (values: { location: string }) => Promise<void>;
  handleSaveDescription: (values: { description: string }) => Promise<void>;
  handleSaveGoal: (
    values: { goal: number },
    campaignId?: number
  ) => Promise<void>;
  handleSaveTheme: (theme: EventTheme) => Promise<void>;
  handleSaveSettings: (values: {
    name: string;
    status: number;
  }) => Promise<void>;
  handleSaveVisibility: (status: number) => Promise<void>;
  handleSaveTime: (values: {
    allDay: boolean;
    startAt: string;
    endAt: string;
    timeZone: string;
  }) => Promise<void>;
}

/**
 * Hook that provides standardized save operations for event edit forms
 */
export const useEventFormSave = ({
  eventId,
  onSuccess,
  onNavigateToMain,
  messageApi,
  keyword = "event",
}: UseEventFormSaveProps): UseEventFormSaveResult => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedEvent, setLoadedEvent] = useState<EventType | null>(null);

  // Display success banner
  const displaySuccessBanner = useCallback(() => {
    messageApi.open({
      type: "success",
      duration: 3,
      icon: <></>,
      content: (
        <div style={{ width: "95vw" }}>
          <Banner
            icon="checkmark"
            text1={`We have successfully updated this ${keyword.toLowerCase()}`}
            textColor="white"
            darkHoverText={true}
            iconBackground={COLORS.SUCCESS_GREEN_DARK}
            uiColor="white"
            style={SUCCESS_BANNER_STYLE}
          />
        </div>
      ),
    });
  }, [messageApi, keyword]);

  // Fetch event data
  const fetchEvent = useCallback(async () => {
    try {
      const response = await eventApi.getById(eventId);
      setLoadedEvent(response.data);
    } catch (err) {
      console.error("Error fetching event:", err);
      setError("Failed to fetch event data");
    }
  }, [eventId]);

  // Generic save handler factory
  const createSaveHandler =
    <T,>(apiCall: (values: T) => Promise<void>) =>
    async (values: T) => {
      setLoading(true);
      setError(null);
      try {
        await apiCall(values);
        onNavigateToMain();
        await fetchEvent();
        displaySuccessBanner();
        onSuccess();
      } catch (err) {
        console.error("Error saving:", err);
        setError("Failed to save changes");
        message.error("Failed to save changes. Please try again.");
      } finally {
        setLoading(false);
      }
    };

  // Individual save handlers
  const handleSaveName = useCallback(
    createSaveHandler(async (values: { name: string }) => {
      await eventApi.patchNameById(eventId, values.name);
    }),
    [eventId]
  );

  const handleSaveLocation = useCallback(
    createSaveHandler(async (values: { location: string }) => {
      await eventApi.patchLocationById(eventId, values.location);
    }),
    [eventId]
  );

  const handleSaveDescription = useCallback(
    createSaveHandler(async (values: { description: string }) => {
      await eventApi.patchDescriptionById(eventId, values.description);
    }),
    [eventId]
  );

  const handleSaveGoal = useCallback(
    async (values: { goal: number }, campaignId?: number) => {
      setLoading(true);
      setError(null);
      try {
        await eventApi.patchGoalById(eventId, values.goal, campaignId);
        onNavigateToMain();
        await fetchEvent();
        displaySuccessBanner();
        onSuccess();
      } catch (err) {
        console.error("Error saving goal:", err);
        setError("Failed to save goal");
        message.error("Failed to save changes. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [eventId, fetchEvent, displaySuccessBanner, onNavigateToMain, onSuccess]
  );

  const handleSaveTheme = useCallback(
    createSaveHandler(async (theme: EventTheme) => {
      await eventApi.patchThemeById(eventId, theme);
    }),
    [eventId]
  );

  const handleSaveSettings = useCallback(
    createSaveHandler(async (values: { name: string; status: number }) => {
      await eventApi.patchSettingsById(eventId, values.name, values.status);
    }),
    [eventId]
  );

  const handleSaveVisibility = useCallback(
    async (status: number) => {
      setLoading(true);
      setError(null);
      try {
        await eventApi.patchVisibilityById(eventId, status);
        onNavigateToMain();
        await fetchEvent();
        displaySuccessBanner();
        onSuccess();
      } catch (err) {
        console.error("Error saving visibility:", err);
        setError("Failed to save visibility");
        message.error("Failed to save changes. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [eventId, onNavigateToMain, fetchEvent, displaySuccessBanner, onSuccess]
  );

  const handleSaveTime = useCallback(
    createSaveHandler(
      async (values: {
        allDay: boolean;
        startAt: string;
        endAt: string;
        timeZone: string;
      }) => {
        await eventApi.patchTimeById(
          eventId,
          values.allDay,
          values.startAt,
          values.endAt,
          values.timeZone
        );
      }
    ),
    [eventId]
  );

  return {
    loading,
    error,
    loadedEvent,
    setLoadedEvent,
    fetchEvent,
    displaySuccessBanner,
    handleSaveName,
    handleSaveLocation,
    handleSaveDescription,
    handleSaveGoal,
    handleSaveTheme,
    handleSaveSettings,
    handleSaveVisibility,
    handleSaveTime,
  };
};

export default useEventFormSave;
